import {
  BuildingOfficeIcon,
  DocumentPlusIcon,
  HashtagIcon,
  ScaleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { memo } from 'react';

import { coverageApiClient } from '@/services/api/coverageService';
import type { CoverageSchema } from '@/services/types/coverage-types';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  createStatusBadge,
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';
import { CarrierLink } from './CarrierLink';

// ============================================================================
// Helpers
// ============================================================================

const getCoverageDisplayName = (coverage: CoverageSchema): string => {
  const carrierName = coverage?.carrier?.name;
  const category = coverage?.category;

  let categoryShorthand = '';
  switch (category?.toUpperCase()) {
    case 'FIRST_PARTY':
      categoryShorthand = '1P';
      break;
    case 'THIRD_PARTY':
      categoryShorthand = '3P';
      break;
    case 'FIRST_PARTY_HEALTH':
      categoryShorthand = '1P Health';
      break;
    default:
      categoryShorthand = coverage?.category || 'Coverage';
  }

  if (carrierName) {
    return `${carrierName} (${categoryShorthand})`;
  }

  return categoryShorthand;
};

// ============================================================================
// Tooltip Content
// ============================================================================

const CoverageTooltipContent = memo<EntityTooltipContentProps<CoverageSchema>>(
  ({ fetchState }) => {
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="coverage" />;
    }

    if (fetchState.status === 'idle' || !fetchState.data) {
      return <EmptyState entityType="coverage" />;
    }

    const coverage = fetchState.data;
    const displayName = getCoverageDisplayName(coverage);

    const policyNumber = coverage.policy_identifiers?.[0]?.identifier_value;
    const claimNumber = coverage.claims?.[0]?.claim_identifier;
    const { status, carrier_v2_id } = coverage;

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader
          icon={DocumentPlusIcon}
          title={displayName}
          badges={status ? createStatusBadge({ value: status }) : undefined}
        />

        <TooltipSection withBorder>
          {carrier_v2_id && (
            <InfoRow
              icon={BuildingOfficeIcon}
              label="Carrier"
              value={
                <CarrierLink entityId={carrier_v2_id} side="left">
                  {coverage.carrier?.name || 'Carrier'}
                </CarrierLink>
              }
            />
          )}
          {policyNumber && (
            <InfoRow icon={HashtagIcon} label="Policy #" value={policyNumber} />
          )}
          {coverage.named_insured && coverage.named_insured.length > 0 && (
            <InfoRow
              icon={UserGroupIcon}
              label="Insured"
              value={coverage.named_insured.join(', ')}
            />
          )}
          {claimNumber && (
            <InfoRow icon={ScaleIcon} label="Claim #" value={claimNumber} />
          )}
        </TooltipSection>
      </div>
    );
  },
);

CoverageTooltipContent.displayName = 'CoverageTooltipContent';

// ============================================================================
// Component
// ============================================================================

export const CoverageLink = createEntityLink<
  CoverageSchema,
  { matterId: string; coverageId: string },
  { matterId: string }
>({
  tooltipContent: CoverageTooltipContent,
  getDetailUrl: (id, { matterId }) => `/matters/${matterId}/coverage/${id}`,
  useQuery: coverageApiClient.endpoints.getCoverage.useQuery,
  getQueryArg: (id, { matterId }) => ({ matterId, coverageId: id }),
  displayName: 'CoverageLink',
});
